import { useEffect, useState } from 'react';
import { useApi } from '../apiHelpers';
import AirspaceStatusPill from '../components/AirspaceStatusPill';
import Tooltip from '../components/ui/Tooltip';
import FadeIn from '../components/ui/FadeIn';
import Skeleton, { SkeletonGroup } from '../components/ui/Skeleton';
import {
  AirspaceStatus,
  ReferenceStatus,
} from '../utils/airspace-helpers';

interface QRDProps {
  eventId: string;
  onClick: () => void;
  // Bump this from the parent to force a refetch — used after EventForm Save
  // so the chip reflects post-invalidation state without a page reload.
  refreshKey?: number;
}

interface ServerData {
  event: {
    airspace_status: AirspaceStatus | null;
    airspace_no_location_ack_at?: string | null;
  };
  activeRefs: Array<{ status: ReferenceStatus }>;
}

/**
 * Small QRD (Quick Reference Display) widget for the event-detail page header.
 *
 *   [ status pill ]   3 of 5 approved · last 2d ago
 *
 * Clicking opens the read-only EventAirspaceModal. This widget is purely
 * informational — it doesn't mutate. Pilots get a "Manage in Pilot Dashboard"
 * link from inside the modal.
 */
const EventAirspaceWidget: React.FC<QRDProps> = ({ eventId, onClick, refreshKey = 0 }) => {
  const api = useApi();
  const [data, setData] = useState<ServerData | null>(null);
  const [loading, setLoading] = useState(true);
  // When the user lacks airspace read access (403) or the fetch fails for any
  // other reason, we render nothing rather than a misleading "Needs Review"
  // chip. This way producers / sales-only users don't see a fake status.
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setForbidden(false);
    api.get(`/airspace/events/${eventId}`, { silent: true })
      .then((d: ServerData) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => {
        if (!cancelled) {
          setForbidden(true);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, refreshKey]);

  // No-data / no-access: render nothing. The event detail page just won't
  // show an airspace chip in the header, which is the desired behavior for
  // anyone outside the pilot/ops/producer/admin set.
  if (forbidden || (!loading && !data)) return null;

  if (loading) {
    return (
      <FadeIn key="loading" className="inline-flex">
        <SkeletonGroup label="Loading airspace" className="inline-flex">
          <Skeleton className="h-6 w-40 rounded-full" />
        </SkeletonGroup>
      </FadeIn>
    );
  }

  const status = (data?.event?.airspace_status || 'needs_review') as AirspaceStatus;
  const refs = data?.activeRefs || [];
  const total = refs.length;
  const approved = refs.filter(r => r.status === 'approved').length;
  const denied = refs.filter(r => r.status === 'denied').length;

  const summary = total === 0
    ? (data?.event?.airspace_no_location_ack_at
        ? 'No location'
        : status === 'complete' ? 'No auth needed' : 'No references yet')
    : `${approved} of ${total} approved${denied > 0 ? ` · ${denied} denied` : ''}`;

  return (
    <FadeIn key="loaded" className="inline-flex">
    <Tooltip label="Open airspace details">
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-2 text-xs text-on-surface hover:text-white bg-gray-800/40 hover:bg-gray-800/70 px-2.5 py-1 rounded-full ring-1 ring-inset ring-gray-700/50 hover:ring-[rgb(70_70_70/0.60)] transition-colors"
      >
        <span className="text-[10px] uppercase tracking-wider text-on-surface-variant">Airspace</span>
        <AirspaceStatusPill status={status} size="table" />
        <span className="text-on-surface-variant">{summary}</span>
      </button>
    </Tooltip>
    </FadeIn>
  );
};

export default EventAirspaceWidget;
